/**
 * Utility functions for detecting and handling macro elements in HTML content
 */

/**
 * Check if a string contains HTML tags
 */
export function isHtml(value: string): boolean {
  if (typeof value !== 'string') return false;
  return /<[a-z][\s\S]*>/i.test(value);
}

/**
 * Check if a string contains macro elements (data-question or data-field attributes)
 */
export function hasMacros(html: string): boolean {
  if (typeof html !== 'string') return false;
  return html.includes('data-question') || html.includes('data-field');
}

/**
 * Check if document content contains any HTML with macros
 */
export function isHtmlTemplate(content: Record<string, unknown>): boolean {
  return Object.values(content).some((value) => {
    if (typeof value === 'string') {
      return hasMacros(value);
    }
    return false;
  });
}

/**
 * Check if an HTML element is a macro element
 */
export function isMacroElement(element: HTMLElement | null): boolean {
  if (!element) return false;
  // Type guard: ensure element is an Element instance (HTMLElement extends Element)
  if (!(element instanceof Element)) return false;
  return (
    element.hasAttribute('data-question') || element.hasAttribute('data-field')
  );
}

/**
 * Find the closest macro element ancestor
 */
export function findMacroAncestor(node: Node | null): HTMLElement | null {
  if (!node) return null;

  let current: Node | null = node;
  while (current && current.nodeType !== Node.DOCUMENT_NODE) {
    if (
      current.nodeType === Node.ELEMENT_NODE &&
      isMacroElement(current as HTMLElement)
    ) {
      return current as HTMLElement;
    }
    current = current.parentNode;
  }
  return null;
}

/**
 * Find adjacent macro element when deleting
 */
export function findAdjacentMacro(
  node: Node | null,
  direction: 'Backspace' | 'Delete'
): HTMLElement | null {
  if (!node) return null;

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);
  const container = range.commonAncestorContainer;

  if (container.nodeType === Node.TEXT_NODE) {
    const textNode = container as Text;
    const offset = range.startOffset;

    if (direction === 'Backspace' && offset === 0) {
      const prevSibling = textNode.previousSibling;
      if (prevSibling && prevSibling.nodeType === Node.ELEMENT_NODE) {
        const element = prevSibling as HTMLElement;
        if (isMacroElement(element)) {
          return element;
        }
      }
    } else if (direction === 'Delete' && offset === textNode.length) {
      const nextSibling = textNode.nextSibling;
      if (nextSibling && nextSibling.nodeType === Node.ELEMENT_NODE) {
        const element = nextSibling as HTMLElement;
        if (isMacroElement(element)) {
          return element;
        }
      }
    }
  }

  if (container.nodeType === Node.ELEMENT_NODE) {
    const element = container as HTMLElement;

    if (isMacroElement(element)) {
      return element;
    }

    const childNodes = Array.from(element.childNodes);
    const cursorPos = range.startOffset;

    if (direction === 'Backspace' && cursorPos > 0) {
      const prevNode = childNodes[cursorPos - 1];
      if (prevNode && prevNode.nodeType === Node.ELEMENT_NODE) {
        const prevElement = prevNode as HTMLElement;
        if (isMacroElement(prevElement)) {
          return prevElement;
        }
      }
    } else if (direction === 'Delete' && cursorPos < childNodes.length) {
      const nextNode = childNodes[cursorPos];
      if (nextNode && nextNode.nodeType === Node.ELEMENT_NODE) {
        const nextElement = nextNode as HTMLElement;
        if (isMacroElement(nextElement)) {
          return nextElement;
        }
      }
    }
  }

  return null;
}

/**
 * Sanitize HTML to prevent XSS while preserving macro attributes
 */
export function sanitizeHtml(html: string): string {
  const temp = document.createElement('div');
  temp.innerHTML = html;

  // Remove potentially dangerous elements
  const dangerousTags = ['script', 'iframe', 'object', 'embed', 'link'];
  dangerousTags.forEach((tag) => {
    const elements = temp.querySelectorAll(tag);
    elements.forEach((el) => el.remove());
  });

  // Remove event handler attributes
  const allElements = temp.querySelectorAll('*');
  allElements.forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      if (attr.name.startsWith('on')) {
        el.removeAttribute(attr.name);
      }
    });
  });

  return temp.innerHTML;
}
